export const ORDER_STATUSES = [
	"CREATED",
	"QUESTIONNAIRE_SUBMITTED",
	"ELIGIBILITY_FAILED",
	"ELIGIBILITY_PASSED",
	"PAYMENT_PENDING",
	"PAYMENT_APPROVED",
	"PAYMENT_FAILED",
	"PAYMENT_RETRY_ALLOWED",
	"PAYMENT_RETRY_EXHAUSTED",
	"PAYMENT_REFUNDED",
	"LAB_REQUIRED",
	"LAB_ORDERED",
	"LAB_KIT_SHIPPED",
	"LAB_KIT_DELIVERED",
	"LAB_SAMPLE_IN_TRANSIT",
	"LAB_SAMPLE_DELIVERED",
	"LAB_SAMPLE_RECEIVED",
	"LAB_RESULTS_PENDING",
	"LAB_RESULTS_REJECTED",
	"LAB_RESULTS_APPROVED",
	"LAB_FAILED",
	"LAB_REORDER_REQUIRED",
	"VISIT_CONFIRMED",
	"VISIT_CANCELLED",
	"VISIT_COMPLETED",
	"PRESCRIPTION_APPROVED",
	"PRESCRIPTION_REJECTED",
	"PHARMACY_DISPATCHED",
	"PHARMACY_DELIVERED",
	"PHARMACY_FAILED",
	"REFILL_ELIGIBLE",
	"REFILL_UPCOMING",
	"REFILL_LOCKED",
	"REFILL_PAYMENT_PENDING",
	"REFILL_RELEASED",
	"REFILL_CANCELLED",
	"CORRECTION_REQUIRED",
	"CORRECTION_IN_PROGRESS",
	"CORRECTION_SUBMITTED",
	"CORRECTION_REJECTED",
	"CORRECTION_APPROVED",
	"CANCELLED",
	"EXPIRED",
	"COMPLETED",
] as const satisfies readonly [string, ...string[]]

export type OrderStatus = (typeof ORDER_STATUSES)[number]

export const INTERNAL_ROLES = ["admin", "ops", "support", "clinician"] as const

export type InternalRole = (typeof INTERNAL_ROLES)[number]

export type Actor =
	| {
			readonly type: "internal"
			readonly userId: string
			readonly role: InternalRole
	  }
	| {
			readonly type: "patient"
			readonly userId: string
	  }

export function isInternalRole(value: string): value is InternalRole {
	return (INTERNAL_ROLES as readonly string[]).includes(value)
}

export const ORDER_COMMAND_TYPES = [
	"create_order",
	"submit_questionnaire",
	"pass_eligibility_screening",
	"fail_eligibility",
	"reset_intake",
	"initiate_payment",
	"approve_payment",
	"fail_payment",
	"allow_payment_retry",
	"exhaust_payment_retries",
	"refund_payment",
	"retry_payment",
	"payment.treatment_updated",
	"payment.shipping_updated",
	"require_lab",
	"order_lab",
	"lab_kit_shipped",
	"lab_kit_delivered",
	"lab_sample_in_transit",
	"lab_sample_delivered",
	"lab_sample_received",
	"lab_results_pending",
	"submit_existing_lab_results",
	"fail_existing_lab_results",
	"approve_uploaded_lab_document",
	"approve_lab_results",
	"reject_lab_results",
	"fail_lab",
	"require_lab_reorder",
	"book_visit",
	"reschedule_visit",
	"cancel_visit",
	"complete_visit",
	"approve_prescription",
	"reject_prescription",
	"dispatch_pharmacy",
	"deliver_pharmacy",
	"fail_pharmacy",
	"schedule_refill",
	"mark_refill_eligible",
	"lock_refill",
	"start_refill_payment",
	"release_refill",
	"cancel_refill",
	"request_correction",
	"start_correction",
	"submit_correction",
	"approve_correction",
	"reject_correction",
	"apply_correction",
	"cancel_order",
	"expire_order",
	"complete_order",
] as const satisfies readonly [string, ...string[]]

export type OrderCommandType = (typeof ORDER_COMMAND_TYPES)[number]

export type OrderCommand = {
	readonly type: OrderCommandType
	readonly reason?: string
	readonly payload?: Record<string, unknown>
}

export function isOrderCommandType(value: string): value is OrderCommandType {
	return (ORDER_COMMAND_TYPES as readonly string[]).includes(value)
}

export const PATIENT_STEP_IDS = [
	"questionnaire",
	"account",
	"gender",
	"eligibility-screening",
	"eligibility-failed",
	"post-auth",
	"shipping",
	"whats-next",
	"membership-agreement",
	"testosterone-agreement",
	"selection",
	"photo-id",
	"lab-results",
	"payment",
	"clinical-review-questionnaire",
	"next-steps",
] as const

export type PatientStepId = (typeof PATIENT_STEP_IDS)[number]

export const WORKFLOW_TEMPLATE_IDS = ["default", "deferred-labs"] as const

export type WorkflowTemplateId = (typeof WORKFLOW_TEMPLATE_IDS)[number]
export type PaymentTiming = "before_lab_order" | "after_lab_results"

export const QUESTIONNAIRE_QUESTION_TYPES = ["select", "multiselect", "text"] as const
export const QUESTIONNAIRE_PHASES = ["pre_account", "hard_eligibility", "clinical_review"] as const
export const QUESTIONNAIRE_GENDERS = ["male", "female", "all"] as const
export const QUESTIONNAIRE_RISK_LEVELS = ["red", "yellow", "regular", "info"] as const

export type QuestionnaireQuestionType = (typeof QUESTIONNAIRE_QUESTION_TYPES)[number]
export type QuestionnairePhase = (typeof QUESTIONNAIRE_PHASES)[number]
export type QuestionnaireGender = (typeof QUESTIONNAIRE_GENDERS)[number]
export type QuestionnairePatientGender = Extract<QuestionnaireGender, "male" | "female">
export type QuestionnaireRiskLevel = (typeof QUESTIONNAIRE_RISK_LEVELS)[number]
export type QuestionnaireResponseKind = "affirmative" | "negative" | "neutral"
export type QuestionnaireInputControl = "default" | "medication_list"

export type QuestionnaireSource = {
	readonly pdf: string
	readonly page: number
	readonly section: string
	readonly questionNumber: string
}

export type QuestionnaireAnswerMetadata = {
	readonly riskLevel: QuestionnaireRiskLevel
	readonly source: QuestionnaireSource
	readonly disqualifies?: boolean | undefined
	readonly responseKind?: QuestionnaireResponseKind | undefined
	readonly exclusive?: boolean | undefined
	readonly displayText?: string | undefined
	readonly aggregateItemText?: string | undefined
}

export type QuestionnaireQuestionPresentation = {
	readonly headingTitle?: string | undefined
	readonly headingSubtitle?: string | undefined
	readonly detailsPromptLabel?: string | undefined
	readonly inputControl?: QuestionnaireInputControl | undefined
	readonly followUpQuestion?: string | undefined
}

export type QuestionnaireStructuredAnswer = {
	readonly value: string | string[]
	readonly details?: string | undefined
}

export type QuestionnaireAnswerValue = string | string[] | QuestionnaireStructuredAnswer
export type QuestionnaireAnswers = Record<string, QuestionnaireAnswerValue>

export type QuestionnaireQuestion = {
	readonly id: string
	readonly question: string
	readonly subtitle?: string | undefined
	readonly type: QuestionnaireQuestionType
	readonly options: readonly string[]
	readonly phase: QuestionnairePhase
	readonly gender: QuestionnaireGender
	readonly riskLevel: QuestionnaireRiskLevel
	readonly source: QuestionnaireSource
	readonly answerMetadata?: Readonly<Record<string, QuestionnaireAnswerMetadata>> | undefined
	readonly disqualifyingAnswers?: readonly string[] | undefined
	readonly detailsPrompt?: string | undefined
	readonly detailsRequiredForAnswers?: readonly string[] | undefined
	readonly presentation?: QuestionnaireQuestionPresentation | undefined
}

export type QuestionnaireTemplate = {
	readonly id: QuestionnaireTemplateId
	readonly version: string
	readonly questions: readonly QuestionnaireQuestion[]
}

export const DEFAULT_QUESTIONNAIRE_TEMPLATE_ID = "intake/v2/t-spray-qualifier" as const
export const DEFAULT_QUESTIONNAIRE_TEMPLATE_VERSION = "v2" as const
export const QUESTIONNAIRE_TEMPLATE_IDS = [DEFAULT_QUESTIONNAIRE_TEMPLATE_ID] as const

export type QuestionnaireTemplateId = (typeof QUESTIONNAIRE_TEMPLATE_IDS)[number]

export const PRODUCT_KEYS = ["t-spray"] as const
export const JOURNEY_DEFINITION_IDS = ["t-spray/default", "t-spray/deferred-labs"] as const
export const JOURNEY_DEFINITION_STATUSES = ["active", "retired"] as const

export type ProductKey = (typeof PRODUCT_KEYS)[number]
export type JourneyDefinitionId = (typeof JOURNEY_DEFINITION_IDS)[number]
export type JourneyDefinitionStatus = (typeof JOURNEY_DEFINITION_STATUSES)[number]

export type WorkflowTemplateReference = {
	readonly id: WorkflowTemplateId
	readonly version: number
}

export type QuestionnaireTemplateReference = {
	readonly id: QuestionnaireTemplateId
	readonly version: string
}

export type JourneyDefinition = {
	readonly id: JourneyDefinitionId
	readonly version: number
	readonly status: JourneyDefinitionStatus
	readonly productKey: ProductKey
	readonly workflow: WorkflowTemplateReference
	readonly questionnaire: QuestionnaireTemplateReference
}
